/**
 * Browser API helpers exposed to Blazor via JSInterop, e.g.
 * await JSRuntime.InvokeVoidAsync("browserUtils.copyToClipboard", "text");
 */

export const browserUtils = {
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  },

  async readFromClipboard(): Promise<string | null> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        return await navigator.clipboard.readText();
      }
      console.warn('Clipboard read not supported');
      return null;
    } catch (error) {
      console.error('Failed to read from clipboard:', error);
      return null;
    }
  },

  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  },

  async showNotification(
    title: string,
    options?: {
      body?: string;
      icon?: string;
      badge?: string;
      tag?: string;
    }
  ): Promise<void> {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return;
    }

    if (Notification.permission !== 'granted') {
      const granted = await this.requestNotificationPermission();
      if (!granted) {
        console.warn('Notification permission denied');
        return;
      }
    }

    new Notification(title, options);
  },

  getBrowserInfo(): {
    userAgent: string;
    platform: string;
    language: string;
    cookiesEnabled: boolean;
    online: boolean;
  } {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      online: navigator.onLine
    };
  },

  getScreenInfo(): {
    width: number;
    height: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
    pixelDepth: number;
  } {
    return {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth
    };
  },

  openInNewTab(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  },

  downloadFile(filename: string, data: string, mimeType: string = 'text/plain'): void {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /** For binary payloads such as a ZIP bundle, which downloadFile's string cannot represent. */
  downloadFileBase64(filename: string, base64: string, mimeType: string = 'application/octet-stream'): void {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  print(): void {
    window.print();
  },

  reload(forceReload: boolean = false): void {
    window.location.reload();
  },

  navigateTo(url: string): void {
    window.location.href = url;
  },

  goBack(): void {
    window.history.back();
  },

  goForward(): void {
    window.history.forward();
  },

  isFeatureSupported(feature: string): boolean {
    return feature in navigator || feature in window;
  },

  getCurrentUrl(): {
    href: string;
    protocol: string;
    hostname: string;
    port: string;
    pathname: string;
    search: string;
    hash: string;
  } {
    return {
      href: window.location.href,
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      port: window.location.port,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    };
  },

  setPageTitle(title: string): void {
    document.title = title;
  },

  getPageTitle(): string {
    return document.title;
  },

  async requestFullscreen(elementId?: string): Promise<boolean> {
    try {
      const element = elementId
        ? document.getElementById(elementId)
        : document.documentElement;

      if (!element) {
        console.warn('Element not found for fullscreen');
        return false;
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Fullscreen request failed:', error);
      return false;
    }
  },

  async exitFullscreen(): Promise<boolean> {
    try {
      if (document.exitFullscreen && document.fullscreenElement) {
        await document.exitFullscreen();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Exit fullscreen failed:', error);
      return false;
    }
  },

  isFullscreen(): boolean {
    return !!document.fullscreenElement;
  },

  // innerWidth/userAgent/scrollToId replace eval-based interop so the CSP needs no 'unsafe-eval'.
  innerWidth(): number {
    return window.innerWidth;
  },

  userAgent(): string {
    return navigator.userAgent;
  },

  scrollToId(id: string, block: ScrollLogicalPosition = 'start'): void {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block });
  }
};

if (typeof window !== 'undefined') {
  (window as any).browserUtils = browserUtils;
}
